import { get, isPlainObject } from "lodash";
import {
  TRIVIA_CONFIG_DEFAULTS,
  TRIVIA_DEFAULT_POINTS,
  TRIVIA_HEX_COLOR_PATTERN,
} from "./trivia-constants";
import type {
  TriviaAnswerModel,
  TriviaConfig,
  TriviaCornerStyle,
  TriviaDensity,
  TriviaIssue,
  TriviaQuestionModel,
  TriviaScoreFormat,
} from "./trivia-types";

export type TriviaParseResult = {
  config: TriviaConfig;
  issues: TriviaIssue[];
};

export type TriviaParseOptions = {
  fallbackTitle?: string;
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function parseString(value: unknown): string | undefined {
  return isNonEmptyString(value) ? value : undefined;
}

function parseBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function parsePositiveNumber(value: unknown, fallback: number): number {
  return isFiniteNumber(value) && value > 0 ? value : fallback;
}

function parseColor(
  value: unknown,
  fallback: string,
  path: string,
  issues: TriviaIssue[],
): string {
  if (value === undefined) {
    return fallback;
  }

  if (typeof value === "string" && TRIVIA_HEX_COLOR_PATTERN.test(value)) {
    return value;
  }

  issues.push({
    level: "warning",
    code: "invalid-color",
    path,
    message: `"${String(value)}" is not a valid hex color; using the default instead.`,
  });

  return fallback;
}

function parseScoreFormat(value: unknown): TriviaScoreFormat {
  return value === "fraction" ||
    value === "percent" ||
    value === "points" ||
    value === "none"
    ? value
    : TRIVIA_CONFIG_DEFAULTS.results.scoreFormat;
}

function parseCornerStyle(value: unknown): TriviaCornerStyle {
  return value === "square"
    ? "square"
    : TRIVIA_CONFIG_DEFAULTS.theme.cornerStyle;
}

function parseDensity(value: unknown): TriviaDensity {
  return value === "compact" ? "compact" : TRIVIA_CONFIG_DEFAULTS.theme.density;
}

function parseAnswer(
  raw: unknown,
  questionIndex: number,
  answerIndex: number,
  issues: TriviaIssue[],
): TriviaAnswerModel | undefined {
  if (!isPlainObject(raw)) {
    return undefined;
  }

  const label = parseString(get(raw, "answer"));
  const media = parseString(get(raw, "media"));
  const path = `trivia.questions[${questionIndex}].answers[${answerIndex}]`;

  if (!label && !media) {
    issues.push({
      level: "error",
      code: "answer-empty",
      path,
      message: "Answer has no text and no image; it was dropped.",
    });
    return undefined;
  }

  return {
    id: `${questionIndex}:${answerIndex}`,
    label: label ?? "",
    correct: parseBoolean(get(raw, "correct"), false),
    media,
    mediaAlt: parseString(get(raw, "mediaAlt")),
  };
}

function parseQuestion(
  raw: unknown,
  questionIndex: number,
  issues: TriviaIssue[],
): TriviaQuestionModel | undefined {
  if (!isPlainObject(raw)) {
    return undefined;
  }

  const path = `trivia.questions[${questionIndex}]`;
  const prompt = parseString(get(raw, "question"));
  const media = parseString(get(raw, "media"));

  if (!prompt && !media) {
    issues.push({
      level: "error",
      code: "question-empty",
      path,
      message: "Question has no text and no image; it was dropped.",
    });
    return undefined;
  }

  const rawAnswers = get(raw, "answers");
  const answers = (Array.isArray(rawAnswers) ? rawAnswers : [])
    .map((a, answerIndex) => parseAnswer(a, questionIndex, answerIndex, issues))
    .filter((a): a is TriviaAnswerModel => !!a);

  if (answers.length < 2) {
    issues.push({
      level: "error",
      code: "question-insufficient-answers",
      path,
      message: `Question has fewer than 2 valid answers (${answers.length}); it was dropped.`,
    });
    return undefined;
  }

  const correctCount = answers.filter((a) => a.correct).length;
  if (correctCount === 0) {
    issues.push({
      level: "warning",
      code: "question-no-correct-answer",
      path,
      message:
        "No answer is marked correct; every choice will score as incorrect.",
    });
  } else if (correctCount > 1) {
    issues.push({
      level: "warning",
      code: "question-multiple-correct-answers",
      path,
      message:
        "More than one answer is marked correct; any of them will score as correct.",
    });
  }

  const rawPoints = get(raw, "points");
  let points = TRIVIA_DEFAULT_POINTS;
  if (rawPoints !== undefined) {
    if (isFiniteNumber(rawPoints) && rawPoints >= 0) {
      points = rawPoints;
    } else {
      issues.push({
        level: "warning",
        code: "question-invalid-points",
        path,
        message: `Points value "${String(rawPoints)}" is invalid; defaulting to ${TRIVIA_DEFAULT_POINTS}.`,
      });
    }
  }

  const timeLimitEnabled = parseBoolean(get(raw, "timeLimitEnabled"), false);
  const rawTimeLimit = get(raw, "timeLimitSeconds");
  const timeLimit = timeLimitEnabled
    ? parsePositiveNumber(rawTimeLimit, TRIVIA_CONFIG_DEFAULTS.timer.seconds)
    : undefined;

  return {
    id: `q${questionIndex}`,
    sourceIndex: questionIndex,
    prompt: prompt ?? "",
    media,
    mediaAlt: parseString(get(raw, "mediaAlt")),
    mediaCaption: parseString(get(raw, "mediaCaption")),
    explanation: parseString(get(raw, "explanation")),
    points,
    timeLimit,
    answers,
  };
}

export function parseTriviaConfig(
  customData: unknown,
  options: TriviaParseOptions = {},
): TriviaParseResult {
  const issues: TriviaIssue[] = [];
  const data = isPlainObject(customData) ? customData : {};

  const rawQuestions = get(data, "trivia.questions");
  const questions = (Array.isArray(rawQuestions) ? rawQuestions : [])
    .map((q, index) => parseQuestion(q, index, issues))
    .filter((q): q is TriviaQuestionModel => !!q);

  if (questions.length === 0) {
    issues.push({
      level: "error",
      code: "no-valid-questions",
      path: "trivia.questions",
      message: "No valid questions are configured for this trivia game.",
    });
  }

  const primaryBackground = parseColor(
    get(data, "primaryColorBackground"),
    TRIVIA_CONFIG_DEFAULTS.theme.primaryBackground,
    "primaryColorBackground",
    issues,
  );
  const primaryText = parseColor(
    get(data, "primaryColorText"),
    TRIVIA_CONFIG_DEFAULTS.theme.primaryText,
    "primaryColorText",
    issues,
  );
  const buttonBackground = parseColor(
    get(data, "buttonColorBackground"),
    TRIVIA_CONFIG_DEFAULTS.theme.buttonBackground,
    "buttonColorBackground",
    issues,
  );
  const buttonText = parseColor(
    get(data, "buttonColorText"),
    TRIVIA_CONFIG_DEFAULTS.theme.buttonText,
    "buttonColorText",
    issues,
  );

  const config: TriviaConfig = {
    questions,
    theme: {
      primaryBackground,
      primaryText,
      buttonBackground,
      buttonText,
      correctColor: parseColor(
        get(data, "trivia.theme.correctColor"),
        TRIVIA_CONFIG_DEFAULTS.theme.correctColor,
        "trivia.theme.correctColor",
        issues,
      ),
      incorrectColor: parseColor(
        get(data, "trivia.theme.incorrectColor"),
        TRIVIA_CONFIG_DEFAULTS.theme.incorrectColor,
        "trivia.theme.incorrectColor",
        issues,
      ),
      // No static default: an unset accent/panel/secondary-text color falls back to the
      // resolved primary/button colors (not a fixed hex), so a post authored before these
      // fields existed renders exactly as it does today.
      accentColor: parseColor(
        get(data, "trivia.theme.accentColor"),
        primaryText,
        "trivia.theme.accentColor",
        issues,
      ),
      panelColor: parseColor(
        get(data, "trivia.theme.panelColor"),
        buttonBackground,
        "trivia.theme.panelColor",
        issues,
      ),
      secondaryTextColor: parseColor(
        get(data, "trivia.theme.secondaryTextColor"),
        primaryText,
        "trivia.theme.secondaryTextColor",
        issues,
      ),
      backgroundMedia: parseString(get(data, "backgroundMedia")),
      logoMedia: parseString(get(data, "trivia.theme.logoMedia")),
      cornerStyle: parseCornerStyle(get(data, "trivia.theme.cornerStyle")),
      density: parseDensity(get(data, "trivia.theme.density")),
      overlayOpacity: TRIVIA_CONFIG_DEFAULTS.theme.overlayOpacity,
    },
    welcome: {
      enabled: parseBoolean(
        get(data, "trivia.welcome.enabled"),
        TRIVIA_CONFIG_DEFAULTS.welcome.enabled,
      ),
      title:
        parseString(get(data, "trivia.settings.title")) ??
        options.fallbackTitle,
      introText: parseString(get(data, "trivia.welcome.introText")),
      startButtonLabel:
        parseString(get(data, "trivia.welcome.startButtonLabel")) ??
        TRIVIA_CONFIG_DEFAULTS.welcome.startButtonLabel,
      media: parseString(get(data, "trivia.welcome.media")),
    },
    timer: {
      // Not customData-driven: the countdown is mandatory by design, so there is no
      // author-facing way to disable it.
      enabled: true,
      seconds: parsePositiveNumber(
        get(data, "timeToAnswer"),
        TRIVIA_CONFIG_DEFAULTS.timer.seconds,
      ),
    },
    feedback: {
      enabled: !parseBoolean(get(data, "trivia.gameplay.hideFeedback"), false),
      autoAdvance: TRIVIA_CONFIG_DEFAULTS.feedback.autoAdvance,
      autoAdvanceSeconds: TRIVIA_CONFIG_DEFAULTS.feedback.autoAdvanceSeconds,
      correctMessage:
        parseString(get(data, "trivia.gameplay.correctMessage")) ??
        TRIVIA_CONFIG_DEFAULTS.feedback.correctMessage,
      incorrectMessage:
        parseString(get(data, "trivia.gameplay.incorrectMessage")) ??
        TRIVIA_CONFIG_DEFAULTS.feedback.incorrectMessage,
      nextLabel:
        parseString(get(data, "trivia.gameplay.nextButtonLabel")) ??
        TRIVIA_CONFIG_DEFAULTS.feedback.nextLabel,
      showDistribution: !parseBoolean(
        get(data, "trivia.gameplay.hideDistribution"),
        false,
      ),
      simulateDistribution: parseBoolean(
        get(data, "trivia.feedback.simulateDistribution"),
        TRIVIA_CONFIG_DEFAULTS.feedback.simulateDistribution,
      ),
    },
    results: {
      heading:
        parseString(get(data, "trivia.results.heading")) ??
        TRIVIA_CONFIG_DEFAULTS.results.heading,
      scoreFormat: parseScoreFormat(get(data, "trivia.results.scoreFormat")),
      message: parseString(get(data, "trivia.results.message")),
      completeLabel: TRIVIA_CONFIG_DEFAULTS.results.completeLabel,
      showChart: !parseBoolean(get(data, "trivia.results.hideChart"), false),
      showQrCode: !parseBoolean(get(data, "trivia.results.hideQrCode"), false),
      qrHeading: parseString(get(data, "trivia.results.qrHeading")),
    },
    progress: {
      visible: !parseBoolean(get(data, "trivia.gameplay.hideProgress"), false),
    },
    randomizeQuestions: parseBoolean(
      get(data, "trivia.gameplay.randomizeQuestions"),
      TRIVIA_CONFIG_DEFAULTS.randomizeQuestions,
    ),
    randomizeAnswers: parseBoolean(
      get(data, "trivia.gameplay.randomizeAnswers"),
      TRIVIA_CONFIG_DEFAULTS.randomizeAnswers,
    ),
    help: {
      enabled: !parseBoolean(get(data, "trivia.gameplay.hideHelp"), false),
      fiftyFifty: !parseBoolean(
        get(data, "trivia.gameplay.hideFiftyFifty"),
        false,
      ),
    },
  };

  if (config.theme.backgroundMedia) {
    config.theme.overlayOpacity = 0.35;
  }

  return { config, issues };
}
